// 3rd Party
import { faFacebookF, faYelp } from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import {
  Div,
  FlexCol,
  FlexRow,
  H2,
  H5,
  P,
} from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import { Icon } from 'opencare-crown';
import { DIMENSIONS } from 'providers/WindowDimensionsProvider';

import GoogleMaps from '../../../common/components/maps/GoogleMaps';
import {
  selectDataClinicAddress,
  selectDataClinicDescription,
  selectDataClinicExternalReviews,
  selectDataClinicHours,
  selectDataClinicName,
  selectDataClinicQuestionResponses,
  selectDataClinicWebsite,
} from '../../connect/selectors';
import { isClinicOpen } from '../../helpers/profile-office-helpers';
import {
  ProfileSubsectionBody,
  ProfileSubsectionHeader,
  ProfileSubsectionTitle,
} from '../../profile-styled-components';
import ClinicHoursList from '../ClinicHoursList/ClinicHoursList';

// Other

import {
  ClinicDescription,
  ClinicQuestionDivider,
  ClinicQuestionResponse,
  ClinicQuestionTitle,
  ExpandButton,
  ExternalLink,
  ExternalLinkListItem,
  SectionDivider,
  StyledContainer,
} from './styled-components';

const ProfileOfficeDetails = ({ layout }) => {
  const address = useSelector(selectDataClinicAddress);
  const clinicDescription = useSelector(selectDataClinicDescription);
  const clinicQuestionResponses = useSelector(
    selectDataClinicQuestionResponses,
  );
  const clinicHours = useSelector(selectDataClinicHours);
  const clinicName = useSelector(selectDataClinicName);
  const clinicWebsite = useSelector(selectDataClinicWebsite);
  const clinicExternalReviews = useSelector(selectDataClinicExternalReviews);

  const [clinicDescriptionExpanded, setClinicDescriptionExpanded] = useState(
    layout !== DIMENSIONS.MOBILE,
  );
  const [
    clinicQuestionsExpandedState,
    setClinicQuestionsExpandedState,
  ] = useState(
    (clinicQuestionResponses || [])
      .map(clinicQuestionResponse => clinicQuestionResponse.question.id)
      .reduce(
        (state, clinicQuestionId) => ({
          ...state,
          [clinicQuestionId]: layout !== DIMENSIONS.MOBILE,
        }),
        {},
      ),
  );

  const facebookLink =
    clinicExternalReviews && clinicExternalReviews.Facebook
      ? clinicExternalReviews.Facebook.sourceProfileUrl
      : null;
  const yelpLink =
    clinicExternalReviews && clinicExternalReviews.Yelp
      ? clinicExternalReviews.Yelp.sourceProfileUrl
      : null;

  const clinicOpen = isClinicOpen(clinicHours);

  // Hide clinic address because there is one at the top near the name.
  const addressVisible = false;

  return (
    <section>
      <ProfileSubsectionHeader>
        <ProfileSubsectionTitle fontSize="24px">
          {clinicName}
        </ProfileSubsectionTitle>
      </ProfileSubsectionHeader>
      <ProfileSubsectionBody>
        {addressVisible
          && (
            <Div>
              <H2 margin="0" fontWeight="300">
                {address.street_number} {address.route}
                {address.suite ? `, ${address.suite}` : ''}
              </H2>
              <P margin="0 0 20px 0">
                {address.locality}, {address.administrative_area_level_1_short}{' '}
                {address.postal_code}
              </P>
            </Div>
          )
        }
        <GoogleMaps singleMarker />
        <Div margin={layout !== DIMENSIONS.MOBILE ? '70px 0' : '30px 0'}>
          <FlexCol>
            <FlexCol>
              {!!clinicDescription
                && (
                  <Div margin="0 0 30px 0">
                    <FlexRow align="center" justify="space-between">
                      <H5 fontSize="16px" margin="0">
                        About
                      </H5>
                      {layout === DIMENSIONS.MOBILE
                        && (
                          <Div margin="0 0 0 15px">
                            <ExpandButton
                              background={colors.blue}
                              color={colors.white}
                              padding="0 0"
                              type="secondary"
                              onClick={() =>
                                setClinicDescriptionExpanded(
                                  !clinicDescriptionExpanded,
                                )
                              }
                            >
                              <Icon
                                type={
                                  clinicDescriptionExpanded === true
                                    ? 'minus'
                                    : 'plus'
                                }
                              />
                            </ExpandButton>
                          </Div>
                        )
                      }
                    </FlexRow>
                    <SectionDivider />
                    {clinicDescriptionExpanded === true
                      && <ClinicDescription>{clinicDescription}</ClinicDescription>
                    }
                  </Div>
                )
              }

              {clinicQuestionResponses && clinicQuestionResponses.length > 0
                && (
                  <Div margin="0 0 30px 0">
                    <FlexRow align="center" justify="space-between">
                      <H5 fontSize="16px" margin="0">
                        FAQ
                      </H5>
                    </FlexRow>
                    <SectionDivider />
                    {clinicQuestionResponses.map((questionResponse, index) => (
                      <Div key={questionResponse.question.id}>
                        <FlexRow
                          align="center"
                          justify="space-between"
                          margin="0 0 10px 0"
                        >
                          <ClinicQuestionTitle>
                            {questionResponse.question.question}?
                          </ClinicQuestionTitle>
                          {layout === DIMENSIONS.MOBILE
                            && (
                              <Div margin="0 0 0 15px">
                                <ExpandButton
                                  background={colors.blue}
                                  color={colors.white}
                                  padding="0 0"
                                  type="secondary"
                                  onClick={() =>
                                    setClinicQuestionsExpandedState(
                                      Object.assign(
                                        {},
                                        clinicQuestionsExpandedState,
                                        {
                                          [questionResponse.question
                                            .id]: !clinicQuestionsExpandedState[
                                            questionResponse.question.id
                                          ],
                                        },
                                      ),
                                    )
                                  }
                                >
                                  <Icon
                                    type={
                                      clinicQuestionsExpandedState[
                                        questionResponse.question.id
                                      ] === true
                                        ? 'minus'
                                        : 'plus'
                                    }
                                  />
                                </ExpandButton>
                              </Div>
                            )
                          }
                        </FlexRow>
                        {clinicQuestionsExpandedState[
                          questionResponse.question.id
                        ] === true
                        && (
                          <ClinicQuestionResponse>
                            {questionResponse.response}
                          </ClinicQuestionResponse>
                        )
                        }
                        {clinicQuestionResponses.length - 1 !== index
                          && <ClinicQuestionDivider />
                        }
                      </Div>
                    ))}
                  </Div>
                )
              }
            </FlexCol>

            <FlexRow mobileDirection="column">
              <StyledContainer margin="0 55px 0 0">
                <FlexRow align="center" justify="space-between">
                  <H5 fontSize="16px" margin="0">
                    Office Hours
                  </H5>
                  {clinicOpen ? <P color={colors.green}>OPEN NOW</P> : null}
                </FlexRow>
                <SectionDivider />
                {clinicHours && <ClinicHoursList hours={clinicHours} />}
              </StyledContainer>

              <StyledContainer
                margin={layout === DIMENSIONS.MOBILE ? '20px 0 0' : ''}
              >
                <H5 fontSize="16px" margin="0">
                  Links
                </H5>
                <SectionDivider />
                <FlexRow flexWrap="wrap" padding="0" as="ul">
                  {clinicWebsite
                    && (
                      <ExternalLinkListItem>
                        <ExternalLink href={clinicWebsite} target="_blank">
                          <FontAwesomeIcon icon={faGlobe} color={colors.blue} />{' '}
                          Website
                        </ExternalLink>
                      </ExternalLinkListItem>
                    )
                  }
                  {facebookLink
                    && (
                      <ExternalLinkListItem>
                        <ExternalLink href={facebookLink} target="_blank">
                          <FontAwesomeIcon
                            icon={faFacebookF}
                            color={colors.blue}
                          />{' '}
                          Facebook
                        </ExternalLink>
                      </ExternalLinkListItem>
                    )
                  }
                  {yelpLink
                    && (
                      <ExternalLinkListItem>
                        <ExternalLink href={yelpLink} target="_blank">
                          <FontAwesomeIcon icon={faYelp} color={colors.blue} />{' '}
                          Yelp
                        </ExternalLink>
                      </ExternalLinkListItem>
                    )
                  }
                </FlexRow>
              </StyledContainer>
            </FlexRow>
          </FlexCol>
        </Div>
      </ProfileSubsectionBody>
    </section>
  );
};

ProfileOfficeDetails.propTypes = {
  layout: PropTypes.string,
};

export default ProfileOfficeDetails;
